import type {
  ChildFeePayment,
  ChildSchoolFees,
  DayAttendanceType,
  ParentChild,
} from "../types/parentFamily";
import { getChildFullName } from "./parentFamilyHelpers";

export function currentAcademicYear(): string {
  return String(new Date().getFullYear());
}

export function dayTypeLabel(dayType?: DayAttendanceType): string {
  switch (dayType) {
    case "full-day":
      return "Full day";
    case "half-day":
      return "Half day";
    default:
      return "—";
  }
}

export function paymentPlanLabel(months: number): string {
  return `${months} Month${months === 1 ? "" : "s"}`;
}

/** Ledger-style currency: "R 32 400" (no decimals, space thousands). */
export function formatLedgerCurrency(amount: number, currency = "ZAR"): string {
  const rounded = Math.round(amount);
  const formatted = new Intl.NumberFormat("en-ZA", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(rounded);
  if (currency === "ZAR") {
    return `R ${formatted.replace(/,/g, " ")}`;
  }
  return formatted;
}

/** Short dd/mm for ledger rows (matches school sample). */
export function formatLedgerDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}`;
}

export function monthlyInstallment(account: ChildSchoolFees): number {
  if (!account.paymentPlanMonths) return 0;
  return account.totalAnnualFee / account.paymentPlanMonths;
}

export function totalPaid(account: ChildSchoolFees): number {
  return account.payments.reduce(
    (sum, payment) => sum + (Number(payment.amountPaid) || 0),
    0
  );
}

export function amountOutstanding(account: ChildSchoolFees): number {
  return Math.max(0, account.totalAnnualFee - totalPaid(account));
}

export function isFeesSettled(account: ChildSchoolFees): boolean {
  return account.feesPaidInFull || amountOutstanding(account) <= 0;
}

export function nextPaymentNumber(account: ChildSchoolFees): number {
  const used = new Set(account.payments.map((p) => p.paymentNumber));
  for (let i = 1; i <= account.paymentPlanMonths; i++) {
    if (!used.has(i)) return i;
  }
  return account.paymentPlanMonths + 1;
}

export interface FeeLedgerRow {
  paymentNumber: number;
  monthLabel: string;
  paidDate?: string;
  amountPaid?: number;
  amountOutstanding?: number;
  receiptNo?: string;
}

/** Build the 12-row payment ledger for display / PDF. */
export function buildFeeLedger(account: ChildSchoolFees): FeeLedgerRow[] {
  const paymentByNumber = new Map(
    [...account.payments]
      .sort((a, b) => a.paymentNumber - b.paymentNumber)
      .map((payment) => [payment.paymentNumber, payment])
  );

  let runningPaid = 0;
  const rows: FeeLedgerRow[] = [];

  for (let i = 1; i <= account.paymentPlanMonths; i++) {
    const payment = paymentByNumber.get(i);
    if (payment) {
      runningPaid += payment.amountPaid;
      rows.push({
        paymentNumber: i,
        monthLabel: `Payment ${i}`,
        paidDate: payment.paidDate,
        amountPaid: payment.amountPaid,
        amountOutstanding: Math.max(0, account.totalAnnualFee - runningPaid),
        receiptNo: payment.receiptNo,
      });
    } else {
      rows.push({
        paymentNumber: i,
        monthLabel: `Payment ${i}`,
      });
    }
  }

  return rows;
}

export function summarizeSchoolFees(account?: ChildSchoolFees) {
  if (!account) {
    return {
      totalAnnualFee: 0,
      totalPaid: 0,
      outstanding: 0,
      paymentCount: 0,
      isSettled: false,
      monthlyInstallment: 0,
    };
  }

  const paid = totalPaid(account);
  const outstanding = amountOutstanding(account);
  return {
    totalAnnualFee: account.totalAnnualFee,
    totalPaid: paid,
    outstanding,
    paymentCount: account.payments.length,
    isSettled: isFeesSettled(account),
    monthlyInstallment: monthlyInstallment(account),
  };
}

export interface SchoolFeeContactInfo {
  phone?: string;
  email?: string;
  website?: string;
}

/** Plain-text notice body for sending the ledger statement. */
export function buildSchoolFeeNotice(
  child: Pick<
    ParentChild,
    "firstName" | "lastName" | "grade" | "classSection"
  >,
  account: ChildSchoolFees
): { title: string; body: string } {
  const childName = getChildFullName(child) || "your child";
  const summary = summarizeSchoolFees(account);
  const ledger = buildFeeLedger(account);
  const paidRows = ledger.filter((row) => row.amountPaid != null);

  const bodyLines = [
    "School Fee Statement",
    "",
    `Student: ${childName}`,
    `Grade: ${child.grade || "—"}`,
    `Day type: ${dayTypeLabel(account.dayType)}`,
    `Payment plan: ${paymentPlanLabel(account.paymentPlanMonths)}`,
    `Total fee for ${account.academicYear}: ${formatLedgerCurrency(summary.totalAnnualFee, account.currency)}`,
    "",
    "Month | Date | Amount paid | Amount outstanding | Receipt No.",
    ...paidRows.map(
      (row) =>
        `${row.monthLabel} | ${formatLedgerDate(row.paidDate)} | ${formatLedgerCurrency(row.amountPaid!, account.currency)} | ${formatLedgerCurrency(row.amountOutstanding!, account.currency)} | ${row.receiptNo || "—"}`
    ),
    "",
    `Amount due: ${formatLedgerCurrency(summary.outstanding, account.currency)}`,
    summary.isSettled
      ? "Status: Paid up for the year — thank you."
      : "",
    "",
    "Please contact the school office if you have any queries.",
  ].filter(Boolean);

  return {
    title: summary.isSettled
      ? `Fee statement: ${childName} — paid up (${account.academicYear})`
      : `Fee statement: ${childName} (${account.academicYear})`,
    body: bodyLines.join("\n"),
  };
}

/** Sort payments for stable display. */
export function sortPayments(payments: ChildFeePayment[]): ChildFeePayment[] {
  return [...payments].sort((a, b) => a.paymentNumber - b.paymentNumber);
}
